import * as _ from "lodash";
import {ASync} from "../PA.Core/Basic/ASync";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {IMessageProvider, IProgressProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {IBusinessObject, IBusinessObjectChanges, IBusinessObjectChangesOptions} from "../PA.Core/PA.Core.IF.BO";
import {ClassificationBOValidation} from "./ClassificationBO.Validation";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {
    CreateOptions,
    ESRecordResolver
} from "../PA.Core/lib/ESRecordResolver"
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_Classification,
    esWB_Classification_Step,
    esWB_Classification_StepR,
    esWB_LE_Spend_Partition_Input,
    esWB_Search_Profile,
    esWB_Search_ProfileCR
} from "../PA.Core/PAdbSchema";
import {
    WB_ClassificationRow,
    WB_ClassificationStepRow,
    WB_ClassificationStepRRow, WB_LESpendPartitionRow,
    WB_SearchProfileCRRow,
    WB_SearchProfileRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbClassification} from "../PA.Core/PAdbSchemaRowsIF";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {Role_User_Admin} from "../PA.Core/Security/Roles";
import {DefaultChanges} from "../PA.Core/BusinessObject/BusinessObjectChanges";
import {BehaviorSubject} from "rxjs";
import {IQueryColumnOptions} from "./SearchBO";

export class ClassificationBO implements IBusinessObject {
    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Classification: WB_ClassificationRow;

    constructor(options?: any) {
        this.CreateOptions = options || {};
        if ('dbContext' in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        }

        this.Identity = new IdentityProvider({id: 0, name: 'New Classification'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider('Validating', this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider('Saving', this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this);

        this.drWB_Classification = null;

        this.IHaveSteps_init()
    }

    //</editor-fold>

    //<editor-fold desc = "IBusinessObject operations">
    get WB_Classification(): WB_ClassificationRow {
        return this.drWB_Classification;
    }

    async LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Classification");
        let olc = self._OnLoadComplete = (async () => {
            self.LoadOptions = options || {};

            try {
                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for new Classification.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return this;
                }

                let newClassificationRow  = <WB_ClassificationRow> await this.dbContext.createRecordWithKey(esWB_Classification)
                newClassificationRow.setDefaults();
                self.drWB_Classification = newClassificationRow;
                self.drWB_Classification.cif.ALGORITHM_TYPE = 'Heuristic'
                self.drWB_Classification.cif.ENTITY_DIMENSION = 'Event'
                self.dbContext.add(esWB_Classification, newClassificationRow);
                self.updateIdentity();

                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;

                self._OnLoadComplete = undefined;
                return self;
            } catch (err) {
                self.LoadProgressProvider.exception(err);
                return self;
            }

        })();
        return olc;
    }

    async Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start('Loading (' + self.Identity.id_ui + ')');
        let olc = this._OnLoadComplete = (async () => {
            try {
                if (self.bIsLoaded) {
                    const sError = 'Classification already loaded.';
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }
                self.LoadOptions = options || {};

                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else if (_.isEmpty(self.dbContext)) {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for Classification.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                if ('RecordResolver' in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver;
                    recordResolverLoadPromise = ASync.resolve();
                } else {
                    recordResolver = ClassificationBO.createRecordResolver({dbContext: self.dbContext});
                    recordResolverLoadPromise = ClassificationBO.setupRecordResolver(recordResolver, [anId]);
                }

                await recordResolverLoadPromise

                const aClassificationRow: any = recordResolver.get(esWB_Classification, anId);

                if (_.isEmpty(aClassificationRow)) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception('Id not found.');
                    return self;
                }

                self.drWB_Classification = aClassificationRow;
                self.updateIdentity();

                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                return self
            } catch (err: any) {
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(err);
                return self;
            }
        })();

        return olc;
    }

    private updateIdentity(): void {

        const {
            WB_CLASSIFICATION_ID,
            CLASSIFICATION_ID,
            CLASSIFICATION_NAME
        } = this.WB_Classification.cif;
        this.Identity.setAll(WB_CLASSIFICATION_ID, CLASSIFICATION_ID, CLASSIFICATION_NAME);
        this.LoadProgressProvider.setName(
            'Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')'
        );
        this.Identity.listenToRecordUpdates(this.WB_Classification, 'WB_CLASSIFICATION_ID', 'CLASSIFICATION_ID', 'CLASSIFICATION_NAME');
        let userContext = PAEnvironment.getValue(PAEnvironment.UserContext, 'UserContext')
        let bHasRole = userContext.hasRoleId(Role_User_Admin)

        if (bHasRole)
            this.Identity.enableRecordWriteBack(this.WB_Classification, undefined, 'CLASSIFICATION_NAME')
        else
            this.Identity.enableRecordWriteBack(this.WB_Classification, undefined, undefined)

    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            'Validating ' + self.WB_Classification.cif.CLASSIFICATION_NAME + '(' + self.WB_Classification.cif.CLASSIFICATION_ID + ')'
        )

        self.ValidateOption = options || {};

        try {
            if (self.dbContext == undefined) {
                const sError = 'Unable to determine dbContext to validate Customer Type.';
                self.ValidateProgressProvider.exception(sError);
                throw sError;
            }

            await ClassificationBOValidation.Validate(self)

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();

        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);

        }
        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: {bSkipValidations?: boolean}): Promise<IBusinessObject> {
        const self = this;
        const wbClassification = <IwbClassification>self.drWB_Classification;
        this.SaveProgressProvider.start(
            'Saving ' + wbClassification.CLASSIFICATION_NAME + '(' + wbClassification.CLASSIFICATION_ID + ')'
        );

        try {
            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save Classification.';
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            if(!options?.bSkipValidations) {
                await self.Validate({})

                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    return self
                }
            }

            await saveFunction(esWB_Classification, self.drWB_Classification, {})
            await saveFunction(esWB_Classification_Step, self.drWB_Classification.WB_ClassificationStepRows, {})
            await saveFunction(esWB_Classification_StepR,
                _.flatMap(self.drWB_Classification.WB_ClassificationStepRows,(a) => a.WB_ClassificationStepRRows), {})

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }

            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);

        } finally {

        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbClassification = <IwbClassification>self.drWB_Classification;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbClassification.CLASSIFICATION_NAME + "(" + wbClassification.CLASSIFICATION_ID + ")")

        _.forEach(self.WB_Classification.WB_ClassificationStepRows, aRow => {
            aRow.markDeleted()
        })

        return new Promise<IBusinessObject>((resolve, reject) => {
            // 1. Validate if we can delete the SalesOrg

            // 2. If Valid, mark all the records for deletion.
            self.drWB_Classification.markDeleted();

            if (bSaveNow) {
                self
                    .SaveUsing(false)
                    .then(ignore => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch(err => {
                        this.DeleteProgressProvider.exception(err);
                        reject(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        let opts = options || {}
        // Set any BO Specific change settings
        return DefaultChanges(this, this.drWB_Classification, opts)
    }


    //</editor-fold>

    //<editor-fold desc = "Classification Steps Operations">
    private _StepsChanged: BehaviorSubject<ClassificationBO>
    private _TagConditionsChanged: BehaviorSubject<ClassificationBO>

    private IHaveSteps_init(): void {
        // Create local BehaviorSubjects
        this._StepsChanged = new BehaviorSubject<ClassificationBO>(undefined)
        this._TagConditionsChanged = new BehaviorSubject<ClassificationBO>(undefined)
    }


    get StepsChanged(): BehaviorSubject<ClassificationBO> {
        return this._StepsChanged
    }

    get TagConditionsChanged(): BehaviorSubject<ClassificationBO> {
        return this._TagConditionsChanged
    }

    addStep(): Promise<IBusinessObject> {
        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            self.dbContext.createRecordWithKey(esWB_Classification_Step)
                .then((aStepRow: WB_ClassificationStepRow) => {

                    aStepRow.setDefaults()
                    aStepRow.cif.WB_CLASSIFICATION_ID = self.WB_Classification.cif.WB_CLASSIFICATION_ID

                    self.dbContext.add(esWB_Classification_Step, aStepRow)
                    self.StepsChanged.next(this)
                    resolve(self)
                }).catch((err) => {
                error(err)
            })
        })
    }

    removeStep(wbClassificationStepId: number): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {
            _.forEach(self.WB_Classification.WB_ClassificationStepRows, (aRow) => {
                if (!aRow.isActive)
                    return

                if (aRow.cif.WB_CLASSIFICATION_STEP_ID == wbClassificationStepId) {
                    aRow.markDeleted()
                }
            })

            self.StepsChanged.next(this)
            resolve(self)
        })
    }

    async addStepR(wbClassificationStepId: number): Promise<IBusinessObject> {

        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            self.dbContext.createRecordWithKey(esWB_Classification_StepR)
                .then((aStepRRow: WB_ClassificationStepRRow) => {

                    aStepRRow.setDefaults()
                    aStepRRow.cif.WB_CLASSIFICATION_STEP_ID = wbClassificationStepId

                    self.dbContext.add(esWB_Classification_StepR, aStepRRow)
                    self.TagConditionsChanged.next(self)
                    resolve(self)
                }).catch((err) => {
                error(err)
            })
        })
    }

    removeStepR(wbClassificationStepId: number,wbClassificationStepRId: number): Promise<IBusinessObject> {
        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {
            let stepRow: WB_ClassificationStepRow = _.first(_.filter(self.drWB_Classification.WB_ClassificationStepRows, aRow => aRow.cif.WB_CLASSIFICATION_STEP_ID == wbClassificationStepId))

            _.forEach(stepRow.WB_ClassificationStepRRows, (aStepRRow: WB_ClassificationStepRRow) => {
                if (aStepRRow.isActive && aStepRRow.cif.WB_CLASSIFICATION_STEPR_ID == wbClassificationStepRId) {
                    aStepRRow.markDeleted()
                }
            })

            self.TagConditionsChanged.next(self)
            resolve(self)
        })
    }

    //</editor-fold>


    //<editor-fold desc = "staticIRecordResolver">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const createOptions: CreateOptions = {
            standardConditions: {
                [esWB_Classification]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Classification_Step]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Classification_StepR]: {"DELETED_IND": {"notIn": ['1']}},
            },
            dctIncludeBelongsToNamedRelations: { }
        };

        return new ESRecordResolver(dbContext, createOptions);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_Classification, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(ClassificationBO, "ClassificationBO", "Classification")
